import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";

import CustomToast from "./CustomToast";
import "./styles.css";

const DEFAULT_DURATION = 4000;
const MAX_VISIBLE = 3;

const ToastContext = createContext(undefined);

let sequence = 0;

const nextId = () => {
  sequence += 1;
  return `hex-toast-${sequence}`;
};

const readOptions = (options) =>
  typeof options === "number" || options == null
    ? { duration: options }
    : options;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const hideToast = useCallback((id) => {
    setToasts((list) =>
      id == null ? [] : list.filter((toast) => toast.id !== id)
    );
  }, []);

  const pushToast = useCallback((type, message, title, options) => {
    if (!message) return null;

    const { duration, action } = readOptions(options);
    const id = nextId();

    setToasts((list) =>
      [
        ...list,
        {
          id,
          type,
          title,
          message,
          action,
          duration: duration ?? DEFAULT_DURATION,
        },
      ].slice(-MAX_VISIBLE)
    );

    return id;
  }, []);

  const showSuccessToast = useCallback(
    (message, title = "Success", options) =>
      pushToast("success", message, title, options),
    [pushToast]
  );

  const showErrorToast = useCallback(
    (message, title = "Error", options) =>
      pushToast("error", message, title, options),
    [pushToast]
  );

  const showWarningToast = useCallback(
    (message, title = "Heads up", options) =>
      pushToast("warning", message, title, options),
    [pushToast]
  );

  const showInfoToast = useCallback(
    (message, title = "Info", options) =>
      pushToast("info", message, title, options),
    [pushToast]
  );

  const value = useMemo(
    () => ({
      showSuccessToast,
      showErrorToast,
      showWarningToast,
      showInfoToast,
      hideToast,
    }),
    [
      showSuccessToast,
      showErrorToast,
      showWarningToast,
      showInfoToast,
      hideToast,
    ]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== "undefined"
        ? createPortal(
            <div className="hex-toast-stack">
              {toasts
                .slice()
                .reverse()
                .map((toast) => (
                  <CustomToast key={toast.id} {...toast} onClose={hideToast} />
                ))}
            </div>,
            document.body
          )
        : null}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }

  return context;
};

let toastRef = null;

export const setToastRef = (ref) => {
  toastRef = ref;
};

export const showSuccessToast = (message, title, options) =>
  toastRef?.showSuccessToast(message, title, options);

export const showErrorToast = (message, title, options) =>
  toastRef?.showErrorToast(message, title, options);

export const showWarningToast = (message, title, options) =>
  toastRef?.showWarningToast(message, title, options);

export const showInfoToast = (message, title, options) =>
  toastRef?.showInfoToast(message, title, options);

export const hideToast = (id) => toastRef?.hideToast(id);
